import {SelfAwarenessResponse, SliceStatus} from 'interfaces';

import {createSlice} from '@reduxjs/toolkit';

import {
  downloadSelfAwarenessPdfFailure,
  downloadSelfAwarenessPdfRequest,
  downloadSelfAwarenessPdfSuccess,
  getSelfAwarenessDataFailure,
  getSelfAwarenessDataRequest,
  getSelfAwarenessDataSuccess,
  getSelfAwarenessPdfFailure,
  getSelfAwarenessPdfRequest,
  getSelfAwarenessPdfSuccess,
  saveSelfAwarenessData,
  saveSelfAwarenessDataFailure,
  saveSelfAwarenessDataSuccess,
} from './selfAwarenessActions';

type SelfAwarenessSliceState = {
  feedback: {
    data: SelfAwarenessResponse;
    status: SliceStatus;
    patientId?: string;
  };
  status: SliceStatus;
  error: string;
};

const defaultSelfAwarenessFeedbackData = {
  result: [],
  hasMore: false,
  nextPage: 0,
  prevPage: 0,
  total: 0,
};

const selfAwarenessSlice = createSlice({
  name: 'selfAwareness',
  initialState: {
    feedback: {
      data: {...defaultSelfAwarenessFeedbackData},
      status: SliceStatus.idle,
    },
    status: SliceStatus.idle,
    error: '',
  } as SelfAwarenessSliceState,
  reducers: {},
  extraReducers: builder => {
    // Helper function to reset feedback data to defaults
    const resetFeedbackData = (state: SelfAwarenessSliceState) => {
      state.feedback.data.result = [];
      state.feedback.data.hasMore = false;
      state.feedback.data.nextPage = 0;
      state.feedback.data.prevPage = 0;
      state.feedback.data.total = 0;
    };

    // Helper function to update feedback data with new payload
    const updateFeedbackData = <T extends SelfAwarenessResponse>(
      state: SelfAwarenessSliceState,
      payload: T,
    ) => {
      const {hasMore, nextPage, prevPage, total} = payload;
      const feedbackData = state.feedback.data;

      feedbackData.hasMore = hasMore;
      feedbackData.nextPage = nextPage;
      feedbackData.prevPage = prevPage;
      feedbackData.total = total;

      // Append new results to existing results using mutable array method
      payload.result.forEach((item: T['result'][number]) => {
        feedbackData.result.push(item);
      });
    };

    builder
      .addCase(getSelfAwarenessDataRequest, (state, action) => {
        const {patientId} = action.payload;
        // Reset data if patient ID has changed
        if (patientId != state.feedback.patientId) {
          resetFeedbackData(state);
        }

        state.feedback.status = SliceStatus.pending;
        state.feedback.patientId = patientId;
      })
      .addCase(getSelfAwarenessDataSuccess, (state, action) => {
        if (typeof action.payload === 'object') {
          updateFeedbackData(state, action.payload);
        } else {
          resetFeedbackData(state);
        }

        state.feedback.status = SliceStatus.resolved;
      })
      .addCase(getSelfAwarenessDataFailure, (state, action) => {
        state.feedback.status = SliceStatus.rejected;
        state.status = SliceStatus.rejected;
        state.error = action.payload;
      })
      .addCase(saveSelfAwarenessData, state => {
        state.status = SliceStatus.pending;
      })
      .addCase(saveSelfAwarenessDataSuccess, state => {
        state.status = SliceStatus.resolved;
      })
      .addCase(saveSelfAwarenessDataFailure, state => {
        state.status = SliceStatus.rejected;
      });
  },
});

export const {reducer: selfAwarenessReducer, name: selfAwarenessReducerName} =
  selfAwarenessSlice;

export type TSelfAwarenessActions =
  | ReturnType<typeof getSelfAwarenessDataFailure>
  | ReturnType<typeof getSelfAwarenessDataRequest>
  | ReturnType<typeof getSelfAwarenessDataSuccess>
  | ReturnType<typeof saveSelfAwarenessData>
  | ReturnType<typeof saveSelfAwarenessDataSuccess>
  | ReturnType<typeof saveSelfAwarenessDataFailure>;

export const selfAwarenessActions = {
  getSelfAwarenessDataFailure,
  getSelfAwarenessDataRequest,
  getSelfAwarenessDataSuccess,
  saveSelfAwarenessData,
  saveSelfAwarenessDataSuccess,
  saveSelfAwarenessDataFailure,
  getSelfAwarenessPdfRequest,
  getSelfAwarenessPdfSuccess,
  getSelfAwarenessPdfFailure,
  downloadSelfAwarenessPdfRequest,
  downloadSelfAwarenessPdfSuccess,
  downloadSelfAwarenessPdfFailure,
};

export type SelfAwarenessState = ReturnType<typeof selfAwarenessReducer>;
